import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { history } from "../store";
import { push } from "react-router-redux";
import { withNamespaces } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB"; // Import the locale
import { fetchCalendarByContract } from "../actions/calendars";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { Grid2x2Check, Info, ChevronLeft } from "lucide-react";

import AppHeader from "../containers/AppHeader";
import { fetchLockers, postPrebooking } from "../actions/qrs";
import NmStepBar from "../components/NmStepBar";
import { QuantityRentalStepper } from "../components/QuantityPickers";
import { currencySymbol } from '../utils/currency';

registerLocale("en-GB", enGB); // Register the locale

const TZ = "Europe/Madrid";

function madrid1500ToUtc(dateObj) {
  // Use only the calendar day, ignore the device timezone/time
  const ymd = format(dateObj, "yyyy-MM-dd"); // e.g. "2025-11-29"
  return fromZonedTime(`${ymd} 15:00:00`, TZ); // -> Date; .toISOString() is UTC
}

function applyBusinessRules(totalAmount, daysSelected, businessRules) {
  // Filter only active "MaxDayDiscount" rules
  const activeRules = businessRules.filter(
    (rule) => rule.Active && rule.Type === "MaxDayDiscount"
  );

  if (!activeRules.length) return { total: totalAmount, applied: null };

  // Sort rules by MaxDays descending (so we check the most restrictive first)
  activeRules.sort((a, b) => b.MaxDays - a.MaxDays);

  // Find the first rule that matches daysSelected
  const matchingRule = activeRules.find((rule) => daysSelected >= rule.MaxDays);

  if (matchingRule) {
    const discount = matchingRule.DiscountPercentage;
    const discountedAmount = totalAmount * (1 - discount / 100);
    return { total: discountedAmount, applied: matchingRule };
  }

  return { total: totalAmount, applied: null };
}

class LockerSelectorView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      customer: null,
      step: 1,
      lockers: null,
      prices: null,
      selectedLockerType: null,
      selectedQuantity: 1,
      startDate: new Date(),
      endDate: new Date(),
      selectedInfo: null, // Stores selected information for display
      qrType: "pay",
      errorMessage: false,
      isDurationExceeded: false,
      priceCalendar: {},
      calendarMonth: new Date(),
      limitDays: 13, // Maximum rental duration in days
      selectedPrices: [],
      totalAmount: 0,
      useUnifiedUI: true, // ← turn unified form ON
      isTypeModalOpen: false,
      isQtyModalOpen: false,
      isDatesModalOpen: false,
      isLoading: false,
      activePanel: "type", // 'type' | 'qty' | 'dates' | null
      businessRules: [],
      appliedRule: null,
      panelLeft: 0,
      contract: null,
      measureModal: false,
      measureType: null,
    };

    this.getLockerPrice = this.getLockerPrice.bind(this);
    this.getLockerType = this.getLockerType.bind(this);
    this.getLockerPicture = this.getLockerPicture.bind(this);

    this.formRef = React.createRef();
    this.typeBtnRef = React.createRef();
    this.qtyBtnRef = React.createRef();
    this.datesStartBtnRef = React.createRef();

    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
  }

  openModal = (key) => this.setState({ [key]: true });
  closeModal = (key) => this.setState({ [key]: false });

  openPanel = (panel) => {
    let btnRef = null;

    if (panel === "type") btnRef = this.typeBtnRef;
    if (panel === "qty") btnRef = this.qtyBtnRef;
    if (panel === "dates") btnRef = this.datesStartBtnRef;

    if (btnRef?.current && this.formRef?.current) {
      const btnRect = btnRef.current.getBoundingClientRect();
      const formRect = this.formRef.current.getBoundingClientRect();
      const left = btnRect.left - formRect.left; // ← correct relative offset

      this.setState({
        activePanel: panel,
        panelLeft: left,
      });
    } else {
      this.setState({ activePanel: panel, panelLeft: 0 });
    }
  };
  closePanel = () => this.setState({ activePanel: null });

  componentDidMount() {
    const { zoneSelected, qr } = this.props.location.state;
    const qrtoken = qr.toString() + zoneSelected.toString();

    this.props
      .fetchLockers(qrtoken)
      .then((answer) => {
        const answerLockers = answer.response.answerLockers;
        this.setState(
          {
            lockers: answerLockers.Disponibility,
            prices: answerLockers.Prices,
            customer: answerLockers.Customer,
            qrType: answerLockers.qrType,
          },
          () => {
            // ✅ Wait until DOM updates, then open panel
            setTimeout(() => {
              if (this.typeBtnRef?.current) {
                this.openPanel("type");
              }
            }, 0);
          }
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  selectLockerType(lockerType) {
    const lockerPrice = this.getLockerPrice(lockerType);
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const { contract } = this.state;

    this.setState({
      selectedLockerType: lockerType,
      selectedInfo: { lockerType, lockerPrice, zoneSelected, descriptionZone },
      step: 2,
      priceCalendar: {},
      ...(this.state.useUnifiedUI ? { isTypeModalOpen: false } : {}),
    });

    // this.props
    //   .fetchCalendarByContract(contract, lockerType, zoneSelected)
    //   .then((response) => {
    //     const calendarArray = response?.response?.PriceCalendar || [];
    //     const rulesFromOperator = (
    //       response?.response?.BusinessRules || []
    //     ).filter((r) => r && r.Active); // removes null and inactive

    //     // 🔥 Flatten the price calendar into a simple { "YYYY-MM-DD": price } object
    //     const flatPriceMap = {};

    //     calendarArray.forEach((entry) => {
    //       const d = new Date(entry.date); // UTC from DB
    //       const isoDate = d.toLocaleDateString("en-CA", {
    //         timeZone: "Europe/Madrid",
    //       });

    //       const matchedPrice = entry.prices.find(
    //         (p) => p.zone === zoneSelected && p.type === lockerType
    //       );

    //       if (matchedPrice) {
    //         flatPriceMap[isoDate] = matchedPrice.price;
    //       }
    //     });

    //     this.setState({
    //       priceCalendar: flatPriceMap,
    //       businessRules: rulesFromOperator,
    //     });
    //   })
    //   .catch((error) => {
    //     console.error("Error fetching calendar data:", error);
    //   });
  }

  goTo(lockerType, zoneSelected, descriptionZone, e) {
    const { qr, qrMode } = this.props.location.state;
    const { lockers, customer } = this.state;

    const lockerPriceChoosed = this.getLockerPrice(lockerType);

    let hobbitAvailable = false;

    Object.keys(lockers).forEach((lockerKey) => {
      if (lockerKey === lockerType) {
        if (lockers[lockerKey].Hobbit === true) {
          hobbitAvailable = true;
        }
      }
    });

    if (hobbitAvailable) {
      this.props.push("/accessibilityView", {
        zoneSelected: zoneSelected,
        descriptionZone: descriptionZone,
        selectedLocker: lockerType,
        lockerPrice: lockerPriceChoosed,
        lockers: this.state.lockers,
        qr: qr,
        qrType: this.state.qrType,
        qrMode: qrMode,
        customer: customer,
      });
    } else {
      const prebooking = {};

      prebooking.Qr = qr;
      prebooking.LockerSelected = lockerType;
      prebooking.Zone = zoneSelected;
      prebooking.Accessibility = false;
      prebooking.qrMode = qrMode;

      this.props
        .postPrebooking(prebooking)
        .then((answer) => {
          const lockerAssigned =
            answer.response.answerPrebooking.LockerAssigned;
          const lockerPin = answer.response.answerPrebooking.LockerPin;
          const lockerMatricula =
            answer.response.answerPrebooking.LockerMatricula;
          const lockerCustomer =
            answer.response.answerPrebooking.LockerCustomer;
          const lockerCustomerID =
            answer.response.answerPrebooking.LockerCustomerID;
          const lockerMatrixId =
            answer.response.answerPrebooking.LockerMatrixId;

          this.props.push("/PhoneView", {
            selectedLocker: lockerType,
            zoneSelected: zoneSelected,
            selectedAccessibility: false,
            descriptionZone: descriptionZone,
            lockerAssigned: lockerAssigned,
            lockerPrice: lockerPriceChoosed,
            lockerPin: lockerPin,
            lockerMatricula: lockerMatricula,
            lockerCustomer: lockerCustomer,
            lockerCustomerID: lockerCustomerID,
            lockerMatrixId: lockerMatrixId,
            qr: qr,
            qrType: this.state.qrType,
            qrMode: qrMode,
            timeStamp: new Date(),
            customer: customer,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  goBack(e) {
    const { qr } = this.props.location.state;

    history.push(`/qrsale/${qr}`);
  }

  renderMonthGrid(monthDate, isFirstMonth) {
    const {
      priceCalendar,
      startDate,
      endDate,
      calendarMonth,
      selectedQuantity,
    } = this.state;
    const { t, i18n } = this.props;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const blanks = Array(
      firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
    ).fill(null); // Monday start

    const weekDays = [
      t("Lun"),
      t("Mar"),
      t("Mie"),
      t("Jue"),
      t("Vie"),
      t("Sab"),
      t("Dom"),
    ];
    const title = firstDay.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const days = [];

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = date.toLocaleDateString("en-CA", {
        timeZone: "Europe/Madrid",
      });
      const price = priceCalendar[iso];

      const isStart =
        startDate && date.toDateString() === startDate.toDateString();
      const isEnd = endDate && date.toDateString() === endDate.toDateString();
      const inRange =
        startDate && endDate && date > startDate && date < endDate;
      const isPast = this.isBeforeToday(date);

      const isSingleDay =
        isStart &&
        isEnd &&
        startDate?.toDateString() === endDate?.toDateString();

      let className = "nm-cal-cell";

      if (isSingleDay) {
        className += " selected single";
      } else {
        if (isStart) className += " selected start";
        if (isEnd) className += " selected end";
        if (inRange) className += " in-range";
      }

      if (isPast || !price) className += " disable";

      days.push(
        <div
          key={iso}
          className={className}
          onClick={() => {
            if (!isPast && price) {
              this.handleCalendarDateClick(new Date(date));
            }
          }}
          title={
            price
              ? `Precio: ${(price * selectedQuantity).toFixed(2)} ${currencySymbol()}`
              : "Sin precio"
          }
        >
          <div className="day">{d}</div>
          <div className="price">
            {price && !isPast
              ? `${(price * selectedQuantity).toFixed(2)} ${currencySymbol()}`
              : "-"}
          </div>
        </div>
      );
    }

    return (
      <div className="nm-cal-month-grid">
        <div className="nm-cal-header">
          <button
            className="nm-cal-nav"
            disabled={this.isCurrentMonthOrBefore(calendarMonth)}
            onClick={() => {
              if (!this.isCurrentMonthOrBefore(calendarMonth)) {
                this.setState({
                  calendarMonth: new Date(
                    calendarMonth.setMonth(calendarMonth.getMonth() - 1)
                  ),
                });
              }
            }}
          >
            &lt;
          </button>
          <h6 className="nm-cal-month">
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </h6>
          <button
            className="nm-cal-nav"
            onClick={() =>
              this.setState({
                calendarMonth: new Date(
                  calendarMonth.setMonth(calendarMonth.getMonth() + 1)
                ),
              })
            }
          >
            &gt;
          </button>
        </div>

        <div className="nm-cal-grid">
          {weekDays.map((day) => (
            <div key={day} className="nm-cal-cell weekday-header">
              {day}
            </div>
          ))}
          {blanks.map((_, i) => (
            <div key={`blank-${i}`} className="nm-cal-cell empty" />
          ))}
          {days}
        </div>
      </div>
    );
  }

  renderMonthGridPC(monthDate, isFirstMonth) {
    const {
      priceCalendar,
      startDate,
      endDate,
      calendarMonth,
      selectedQuantity,
    } = this.state;
    const { t, i18n } = this.props;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const blanks = Array(
      firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
    ).fill(null); // Monday start

    const weekDays = [
      t("Lun"),
      t("Mar"),
      t("Mie"),
      t("Jue"),
      t("Vie"),
      t("Sab"),
      t("Dom"),
    ];
    const title = firstDay.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const days = [];

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = date.toLocaleDateString("en-CA", {
        timeZone: "Europe/Madrid",
      });
      const price = priceCalendar[iso];

      const isStart =
        startDate && date.toDateString() === startDate.toDateString();
      const isEnd = endDate && date.toDateString() === endDate.toDateString();
      const inRange =
        startDate && endDate && date > startDate && date < endDate;
      const isPast = this.isBeforeToday(date);

      const isSingleDay =
        isStart &&
        isEnd &&
        startDate?.toDateString() === endDate?.toDateString();

      let className = "nm-cal-cell";

      if (isSingleDay) {
        className += " selected single";
      } else {
        if (isStart) className += " selected start";
        if (isEnd) className += " selected end";
        if (inRange) className += " in-range";
      }

      if (isPast || !price) className += " disable";

      days.push(
        <div
          key={iso}
          className={className}
          onClick={() => {
            if (!isPast && price) {
              this.handleCalendarDateClick(new Date(date));
            }
          }}
          title={price ? `Precio: ${price * selectedQuantity} ${currencySymbol()}` : "Sin precio"}
        >
          <div className="day">{d}</div>
          <div className="price">
            {price ? `${price * selectedQuantity} ${currencySymbol()}` : "-"}
          </div>
        </div>
      );
    }

    return (
      <div className="nm-cal-month-grid">
        <div className="nm-cal-header">
          {isFirstMonth && (
            <button
              className="nm-cal-nav"
              disabled={this.isCurrentMonthOrBefore(calendarMonth)}
              onClick={() => {
                if (!this.isCurrentMonthOrBefore(calendarMonth)) {
                  this.setState({
                    calendarMonth: new Date(
                      calendarMonth.setMonth(calendarMonth.getMonth() - 1)
                    ),
                  });
                }
              }}
            >
              &lt;
            </button>
          )}
          <h6 className="nm-cal-month">
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </h6>
          {!isFirstMonth && (
            <button
              className="nm-cal-nav"
              onClick={() =>
                this.setState({
                  calendarMonth: new Date(
                    calendarMonth.setMonth(calendarMonth.getMonth() + 1)
                  ),
                })
              }
            >
              &gt;
            </button>
          )}
        </div>

        <div className="nm-cal-grid">
          {weekDays.map((day) => (
            <div key={day} className="nm-cal-cell weekday-header">
              {day}
            </div>
          ))}
          {blanks.map((_, i) => (
            <div key={`blank-${i}`} className="nm-cal-cell empty" />
          ))}
          {days}
        </div>
      </div>
    );
  }

  renderPriceCalendarPC() {
    const { calendarMonth } = this.state;

    const nextMonth = new Date(calendarMonth);
    nextMonth.setMonth(calendarMonth.getMonth() + 1);

    return (
      <div className="nm-cal-dual">
        {this.renderMonthGridPC(calendarMonth, true)}
        {this.renderMonthGridPC(nextMonth, false)}
      </div>
    );
  }

  renderPriceCalendar() {
    const { calendarMonth } = this.state;

    const nextMonth = new Date(calendarMonth);
    nextMonth.setMonth(calendarMonth.getMonth() + 1);

    return (
      <div className="nm-cal-dual">
        {this.renderMonthGrid(calendarMonth, true)}
      </div>
    );
  }

  handleCalendarDateClick = (clickedDate) => {
    const { startDate, endDate, priceCalendar, selectedQuantity } = this.state;

    if (startDate && clickedDate.toDateString() === startDate.toDateString()) {
      this.setState({
        startDate: null,
        endDate: null,
        selectedPrices: [],
        totalAmount: 0,
        isDurationExceeded: false,
      });
      return;
    } else if (!startDate || clickedDate < startDate) {
      const iso = clickedDate.toLocaleDateString("en-CA", {
        timeZone: "Europe/Madrid",
      });
      const base = priceCalendar[iso] || 0;
      const dayPrice = base * (selectedQuantity || 1);

      this.setState({
        startDate: clickedDate,
        endDate: clickedDate, // same day
        isDurationExceeded: false,
        selectedPrices: base
          ? [{ date: new Date(clickedDate), price: dayPrice }]
          : [],
        totalAmount: base ? dayPrice : 0,
      });
      return;
    } else {
      const diff = Math.ceil((clickedDate - startDate) / (1000 * 60 * 60 * 24));
      const tooLong = diff > this.state.limitDays;

      if (!tooLong) {
        const selectedPrices = [];
        let totalAmount = 0;

        for (
          let d = new Date(startDate);
          d <= clickedDate;
          d.setDate(d.getDate() + 1)
        ) {
          const iso = d.toLocaleDateString("en-CA", {
            timeZone: "Europe/Madrid",
          });
          const price = priceCalendar[iso];
          if (price) {
            const dayPrice = price * selectedQuantity;
            selectedPrices.push({ date: new Date(d), price: dayPrice });
            totalAmount += dayPrice;
          }
        }

        // ✅ Apply business rules discount here
        const daysSelected = selectedPrices.length;
        const { total, applied } = applyBusinessRules(
          totalAmount,
          daysSelected,
          this.state.businessRules
        );

        this.setState({
          endDate: clickedDate,
          isDurationExceeded: tooLong,
          selectedPrices,
          totalAmount: total,
          appliedRule: applied,
        });
      } else {
        this.setState({ endDate: clickedDate, isDurationExceeded: true });
      }
    }
  };

  handleQuantityChange(event) {
    this.setState({
      selectedQuantity: event.target.value,
      startDate: null,
      endDate: null,
      selectedPrices: [],
      totalAmount: 0,
      isDurationExceeded: false,
    });
  }

  handleStartDateChange(date) {
    const { endDate } = this.state;
    const localDate = new Date(date.setHours(15, 0, 0, 0)); // Set to the start of the day in local time

    if (endDate) {
      const differenceInDays = Math.ceil(
        (endDate - localDate) / (1000 * 60 * 60 * 24)
      );
      this.setState({
        startDate: localDate,
        isDurationExceeded: differenceInDays >= this.state.limitDays, // Show message if duration exceeds 14 days
      });
    } else {
      this.setState({ startDate: localDate });
    }
  }

  handleEndDateChange(date) {
    const { startDate } = this.state;
    const localDate = new Date(date.setHours(15, 0, 0, 0)); // Set to the start of the day in local time

    if (startDate) {
      const differenceInDays = Math.ceil(
        (localDate - startDate) / (1000 * 60 * 60 * 24)
      );
      this.setState({
        endDate: localDate,
        isDurationExceeded: differenceInDays >= this.state.limitDays, // Show message if duration exceeds 14 days
      });
    } else {
      this.setState({ endDate: localDate });
    }
  }

  isAfterCutoffTime() {
    const nowInSpain = new Date().toLocaleString("en-US", {
      timeZone: "Europe/Madrid",
    });
    const currentHourInSpain = new Date(nowInSpain).getHours();
    return currentHourInSpain >= 18;
  }

  handleContinue() {
    if (this.state.isLoading) return;
    this.setState({ isLoading: true });
    const { qr, qrMode, zoneSelected, descriptionZone } = this.props.location.state;
    const { qrType, lockers, customer, selectedLockerType } = this.state;

    const lockerPriceChoosed = this.getLockerPrice(selectedLockerType);

    let hobbitAvailable = false;

    Object.keys(lockers).forEach((lockerKey) => {
      if (lockerKey === selectedLockerType) {
        if (lockers[lockerKey].Hobbit === true) {
          hobbitAvailable = true;
        }
      }
    });

    if (hobbitAvailable) {
      this.props.push("/accessibilityView", {
        zoneSelected: zoneSelected,
        descriptionZone: descriptionZone,
        selectedLocker: selectedLockerType,
        lockerPrice: lockerPriceChoosed,
        lockers: this.state.lockers,
        qr: qr,
        qrType: qrType,
        qrMode: qrMode,
        customer: customer,
      });
    } else {
      const prebooking = {};

      prebooking.Qr = qr;
      prebooking.LockerSelected = selectedLockerType;
      prebooking.Zone = zoneSelected;
      prebooking.Accessibility = false;
      prebooking.qrMode = qrMode;

      this.props
        .postPrebooking(prebooking)
        .then((answer) => {
          const lockerAssigned =
            answer.response.answerPrebooking.LockerAssigned;
          const lockerPin = answer.response.answerPrebooking.LockerPin;
          const lockerMatricula =
            answer.response.answerPrebooking.LockerMatricula;
          const lockerCustomer =
            answer.response.answerPrebooking.LockerCustomer;
          const lockerCustomerID =
            answer.response.answerPrebooking.LockerCustomerID;
          const lockerMatrixId =
            answer.response.answerPrebooking.LockerMatrixId;

          this.props.push("/PhoneView", {
            selectedLocker: selectedLockerType,
            zoneSelected: zoneSelected,
            selectedAccessibility: false,
            descriptionZone: descriptionZone,
            lockerAssigned: lockerAssigned,
            lockerPrice: lockerPriceChoosed,
            lockerPin: lockerPin,
            lockerMatricula: lockerMatricula,
            lockerCustomer: lockerCustomer,
            lockerCustomerID: lockerCustomerID,
            lockerMatrixId: lockerMatrixId,
            qr: qr,
            qrType: qrType,
            qrMode: qrMode,
            timeStamp: new Date(),
            customer: customer,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  getLockerType(type) {
    const { t, i18n } = this.props;

    switch (type) {
      case "BNS":
        return "Standard";
      case "CNS":
        return "Standard Carga";
      case "BWS":
        return "Standard XL";
      case "BNM":
        return "Medium";
      case "CNM":
        return "Medium Carga";
      case "BWM":
        return "Medium XL";
      case "CWM":
        return "Medium Carga XL";
      case "BNL":
        return "Large";
      case "CNL":
        return "Large Carga";
      case "BWL":
        return "Large XL";
      case "CWL":
        return "Large Carga XL";
      case "BNXL":
        return "Large";
      case "CNXL":
        return "Large Carga";
      case "BWXL":
        return "Large XL";
      case "CWXL":
        return "Large Carga XL";
      case "BN2XL":
        return "Large";
      case "CN2XL":
        return "Large Carga";
      case "BW2XL":
        return "Large XL";
      case "CW2XL":
        return "Large Carga XL";
      case "BN3XL":
        return "Ski";
      case "CN3XL":
        return "Ski Carga";
      case "BW3XL":
        return "Ski XL";
      case "BW3XL":
        return "Ski Carga XL";
      default:
        return "None";
    }
  }

  getLockerPicture(type) {
    return `/images/measures/locker_final_${type}.png`;
  }

  getLockerPcPicture(type) {
    return `/images/measures/locker_final_${type}.png`;
  }

  getLockerDimensions(type) {
    const dims = {
      'BNS':   { ancho: 30, alto: 30,  fondo: 50 },
      'CNS':   { ancho: 30, alto: 30,  fondo: 50 },
      'BWS':   { ancho: 40, alto: 30,  fondo: 50 },
      'CWS':   { ancho: 40, alto: 30,  fondo: 50 },
      'BNM':   { ancho: 30, alto: 60,  fondo: 50 },
      'CNM':   { ancho: 30, alto: 60,  fondo: 50 },
      'BWM':   { ancho: 40, alto: 60,  fondo: 50 },
      'CWM':   { ancho: 40, alto: 60,  fondo: 50 },
      'BNL':   { ancho: 30, alto: 90,  fondo: 50 },
      'CNL':   { ancho: 30, alto: 90,  fondo: 50 },
      'BWL':   { ancho: 60, alto: 90,  fondo: 50 },
      'CWL':   { ancho: 60, alto: 90,  fondo: 50 },
      'BNXL':  { ancho: 30, alto: 120, fondo: 50 },
      'CNXL':  { ancho: 30, alto: 120, fondo: 50 },
      'BWXL':  { ancho: 40, alto: 120, fondo: 50 },
      'CWXL':  { ancho: 40, alto: 120, fondo: 50 },
      'BN2XL': { ancho: 30, alto: 150, fondo: 50 },
      'CN2XL': { ancho: 30, alto: 150, fondo: 50 },
      'BW2XL': { ancho: 40, alto: 150, fondo: 50 },
      'CW2XL': { ancho: 40, alto: 150, fondo: 50 },
      'BN3XL': { ancho: 30, alto: 190, fondo: 50 },
      'CN3XL': { ancho: 30, alto: 190, fondo: 50 },
      'BW3XL': { ancho: 40, alto: 190, fondo: 50 },
      'CW3XL': { ancho: 40, alto: 190, fondo: 50 },
    };
    return dims[type] || null;
  }

  getLockerUseCase(type) {
    const d = this.getLockerDimensions(type);
    if (!d) return null;
    const { ancho, alto } = d;
    const wide = ancho >= 40;

    if (alto <= 30) {
      return {
        label: wide ? "Artículos compactos y anchos" : "Artículos pequeños",
        desc: wide
          ? "Diseñada para cascos de bici o moto, bolsos de mano anchos y artículos personales voluminosos."
          : "Ideal para cascos de bicicleta, bolsos pequeños y artículos personales de bolsillo.",
        fits: wide
          ? ["🪖 Casco bici / moto", "👜 Bolso de mano", "📦 Artículos pequeños"]
          : ["🪖 Casco bicicleta", "👜 Bolso pequeño", "🎒 Mochila mini"],
      };
    }
    if (alto <= 60) {
      return {
        label: wide ? "Mochila urbana grande" : "Mochila urbana",
        desc: wide
          ? "Cabe una mochila de ciudad amplia, bolsa de la compra o bolso de hombro de gran tamaño."
          : "Perfecta para mochila de portátil, bolsa de tela o chaqueta doblada.",
        fits: wide
          ? ["🎒 Mochila urbana", "🛍️ Bolsa de compras", "👜 Bolso grande"]
          : ["🎒 Mochila portátil", "🧥 Chaqueta", "🛍️ Bolsa de tela"],
      };
    }
    if (alto <= 90) {
      return {
        label: wide ? "Maleta de cabina y mochilas grandes" : "Mochilas grandes y deporte",
        desc: wide
          ? "Admite mochilas de gran tamaño y maletas de cabina estándar (hasta 55 cm de alto, 40 cm de ancho)."
          : "Caben mochilas de trekking, bolsas de deporte y equipo de montaña.",
        fits: wide
          ? ["🧳 Maleta cabina", "🎒 Mochila grande", "⛷️ Botas de esquí"]
          : ["🎒 Mochila trekking", "🏋️ Bolsa de deporte", "🏕️ Equipo montaña"],
      };
    }
    if (alto <= 120) {
      return {
        label: wide ? "Maleta mediana" : "Equipaje alto y estrecho",
        desc: wide
          ? "Perfecta para maletas medianas de cabina y bolsas de deporte de gran tamaño."
          : "Indicada para maletas delgadas en posición vertical o bolsas de esquí estrechas.",
        fits: wide
          ? ["🧳 Maleta mediana", "🏋️ Bolsa deporte XL", "🎿 Bolsa de esquí"]
          : ["🧳 Maleta delgada", "🎿 Esquís", "🏄 Tabla estrecha"],
      };
    }
    if (alto <= 150) {
      return {
        label: wide ? "Maleta grande de facturación" : "Maleta alta y deporte",
        desc: wide
          ? "Para maletas grandes de facturación y equipos deportivos voluminosos."
          : "Para maletas altas y estrechas, bolsas de viaje largas o estuches de instrumentos.",
        fits: wide
          ? ["🧳 Maleta grande", "🎿 Equipo esquí", "🛹 Skateboard"]
          : ["🧳 Maleta alta", "🎸 Estuche instrumento", "🏋️ Equipo deportivo"],
      };
    }
    return {
      label: "Equipaje extra grande",
      desc: wide
        ? "Nuestra taquilla más grande. Admite maletas enormes, tablas de surf, bolsas de ski o bicicletas plegadas."
        : "Para equipos de gran formato: esquís, tablas largas, instrumentos o maletas extra altas.",
      fits: wide
        ? ["🧳 Maleta XXL", "🏄 Tabla de surf", "🚲 Bici plegable", "🎿 Bolsa de ski"]
        : ["🎿 Esquís", "🏄 Tabla larga", "🎻 Instrumento largo", "🧳 Maleta extra alta"],
    };
  }

  getLockerPrice(type) {
    const { prices } = this.state;
    const lockerPriceObj = prices.find(
      (price) => price.item === `LOCKER${type}`
    );

    let lockerPrice = 0;

    if (lockerPriceObj) {
      lockerPrice = Number(lockerPriceObj.price);
    }

    return lockerPrice.toFixed(2);
  }

  renderLockers() {
    const { t } = this.props;
    const { lockers, prices, selectedLockerType } = this.state;

    const orderedLockerKeys = [
      "BNS", "CNS", "BWS", "CWS",
      "BNM", "CNM", "BWM", "CWM",
      "BNL", "CNL", "BWL", "CWL",
      "BNXL", "CNXL", "BWXL", "CWXL",
      "BN2XL", "CN2XL", "BW2XL", "CW2XL",
      "BN3XL", "CN3XL", "BW3XL", "CW3XL",
    ];

    if (!lockers || !prices) {
      return (
        <div className="spinner">
          <img src="/images/logo.png" alt="Logo" />
        </div>
      );
    }

    const cards = orderedLockerKeys.filter((k) => lockers[k]);

    if (cards.length === 0) {
      return (
        <div className="nm-sold-out" onClick={(e) => this.goBack(e)}>
          <h5>{t("TAQUILLAS AGOTADAS")}</h5>
          <h6>{t("Haz click aquí para volver a selección de zonas")}</h6>
          <div className="lockSoldOutImgContainer">
            <img className="lockSoldOutImg" src="/images/locker_BNS.png" alt="Locker" />
            <img className="lockSoldOutCross" src="/images/cross.png" alt="Cross" />
          </div>
        </div>
      );
    }

    const fewCards = cards.length < 4;

    return (
      <div className={`nm-locker-grid${fewCards ? " nm-locker-grid-few" : ""}`}>
        {cards.map((lockerKey) => {
          const d = this.getLockerDimensions(lockerKey);
          return (
            <div
              key={lockerKey}
              className={`nm-locker-card${selectedLockerType === lockerKey ? " nm-locker-card-selected" : ""}`}
              onClick={() => this.selectLockerType(lockerKey)}
            >
              <button
                className="nm-locker-info-btn"
                onClick={(e) => { e.stopPropagation(); this.setState({ measureModal: true, measureType: lockerKey }); }}
                title={t("Ver información")}
              >
                <Info size={13} strokeWidth={2.5} />
              </button>
              <div className="nm-locker-img-wrap">
                <img src={this.getLockerPicture(lockerKey)} alt={lockerKey} className="nm-locker-img" />
              </div>
              <span className="nm-locker-type">{this.getLockerType(lockerKey)}</span>
              {d && (
                <span className="nm-locker-dims">{d.ancho} × {d.alto} × {d.fondo} cm</span>
              )}
              <span className="nm-price">{this.getLockerPrice(lockerKey)} {currencySymbol()}</span>
            </div>
          );
        })}
      </div>
    );
  }

  renderMeasureModal() {
    const { t } = this.props;
    const { measureModal, measureType } = this.state;
    if (!measureModal || !measureType) return null;

    const d = this.getLockerDimensions(measureType);
    const useCase = this.getLockerUseCase(measureType);
    const close = () => this.setState({ measureModal: false, measureType: null });

    return (
      <div className="nm-modal-overlay" onClick={close}>
        <div className="nm-modal nm-info-modal" onClick={(e) => e.stopPropagation()}>

          {/* ── Hero ── */}
          <div className="nm-info-hero">
            <button className="nm-info-back" onClick={close}>
              <ChevronLeft size={18} strokeWidth={2.5} />
              {t("Volver")}
            </button>
            <img
              src={this.getLockerPicture(measureType)}
              alt={measureType}
              className="nm-info-hero-img"
            />
            <h4 className="nm-info-hero-title">{this.getLockerType(measureType)}</h4>
            <span className="nm-info-hero-price">{this.getLockerPrice(measureType)} {currencySymbol()}</span>
          </div>

          {/* ── Dimensions ── */}
          {d && (
            <div className="nm-info-dims">
              <div className="nm-info-dim-block">
                <span className="nm-info-dim-value">{d.ancho}</span>
                <span className="nm-info-dim-unit">cm</span>
                <span className="nm-info-dim-label">{t("Ancho")}</span>
              </div>
              <div className="nm-info-dim-sep">×</div>
              <div className="nm-info-dim-block">
                <span className="nm-info-dim-value">{d.alto}</span>
                <span className="nm-info-dim-unit">cm</span>
                <span className="nm-info-dim-label">{t("Alto")}</span>
              </div>
              <div className="nm-info-dim-sep">×</div>
              <div className="nm-info-dim-block">
                <span className="nm-info-dim-value">{d.fondo}</span>
                <span className="nm-info-dim-unit">cm</span>
                <span className="nm-info-dim-label">{t("Fondo")}</span>
              </div>
            </div>
          )}

          {/* ── Use case ── */}
          {useCase && (
            <div className="nm-info-usecase">
              <div className="nm-info-usecase-header">
                <div className="nm-info-usecase-icon">📦</div>
                <span className="nm-info-usecase-heading">{t("¿Qué cabe en esta taquilla?")}</span>
              </div>
              <span className="nm-info-usecase-label">{t(useCase.label)}</span>
              <p className="nm-info-usecase-desc">{t(useCase.desc)}</p>
              <div className="nm-info-fits">
                {useCase.fits.map((item, i) => (
                  <span key={i} className="nm-info-fit-tag">{t(item)}</span>
                ))}
              </div>
            </div>
          )}

          <div className="nm-info-footer" />

        </div>
      </div>
    );
  }

  renderLockersPc() {
    return this.renderLockers();
  }

  isBeforeToday(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time
    return date < today;
  }

  isBeforeStart(date) {
    const { startDate } = this.state;

    const today = new Date(startDate);
    today.setHours(0, 0, 0, 0); // Remove time
    return date < today;
  }

  isCurrentMonthOrBefore(date) {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    return date.getFullYear() <= year && date.getMonth() <= month;
  }

  renderUnifiedForm(mode = "mobile") {
    const { t } = this.props;
    const {
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      totalAmount,
      appliedRule,
    } = this.state;

    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);
    const canContinue =
      selectedLockerType &&
      selectedQuantity &&
      startDate &&
      !isDurationExceeded;

    return (
      <div className="nm-form">
        {/* TYPE */}
        <div className="nm-uf-row">
          <button
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openModal("isTypeModalOpen")}
          >
            <label className="nm-uf-label">{t("Tipo")}</label>
            {selectedLockerType
              ? this.getLockerType(selectedLockerType)
              : t("Selecciona...")}
          </button>
        </div>

        {/* QUANTITY */}
        <div className="nm-uf-row">
          <button
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openModal("isQtyModalOpen")}
            disabled={(!selectedLockerType) || true}
          >
            <label className="nm-uf-label">{t("Cantidad")}</label>
            {qty} {qty === 1 ? " " + t("taquilla") : " " + t("taquillas")}
          </button>
        </div>

        {/* DATES */}
        <div className="nm-uf-row">
          <div className="d-flex">
            <div className="col">
              <button
                type="button"
                className="nm-uf-btn"
                onClick={() => this.openModal("isDatesModalOpen")}
                disabled={(!selectedLockerType) || true}
              >
                <label className="nm-uf-label">{t("Inicio")}</label>
                {startDate
                  ? `${startDate.toLocaleDateString()}`
                  : t("DD/MM/YYYY")}
              </button>
            </div>

            <vr class="vr"></vr>

            <div className="col">
              <button
                type="button"
                className="nm-uf-btn"
                onClick={() => this.openModal("isDatesModalOpen")}
                disabled={(!selectedLockerType || !startDate) || true}
              >
                <label className="nm-uf-label">{t("Fin")}</label>
                {endDate ? `${endDate.toLocaleDateString()}` : t("DD/MM/YYYY")}
              </button>
            </div>
          </div>
        </div>

        {/* TOTAL 
          <div className="nm-uf-row">
            <label className="nm-uf-label">{t('Importe')}</label>
            <button
              type="button"
              className="nm-uf-btn"
            >
              {totalAmount.toFixed(2)} €
            </button>
          </div>*/}

        {/* ERROR MESSAGE */}
        {this.state.errorMessage && (
          <div className="text-danger text-center">
            {t(
              "LO SENTIMOS PERO ESTAS FECHAS NO ESTÁN DISPONIBLES, PRUEBA OTRAS U OTRO TIPO DE TAQUILLAS."
            )}
          </div>
        )}

        {/*DISCOUNT*/}
        {appliedRule && (
          <span className="text-center">
            {t("Descuento aplicado: {{discount}}% (desde los {{days}} días)", {
              discount: appliedRule.DiscountPercentage,
              days: appliedRule.MaxDays,
            })}
          </span>
        )}

        {/* CONTINUE */}
        <div className="nm-form-actions">
          <button
            className={`nm-btn-continue ${!canContinue || this.state.isLoading ? "disabled" : ""}`}
            disabled={!canContinue || this.state.isLoading}
            onClick={() => this.handleContinue()}
          >
            {this.state.isLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <>
                {t("Continuar")}
                {t(" por ")}
                <br />
                {Number(this.getLockerPrice(selectedLockerType)).toFixed(2)}
                {` ${currencySymbol()}`}
              </>
            )}
          </button>
        </div>

        {this.renderUnifiedModals()}
      </div>
    );
  }

  renderUnifiedModals() {
    const { t } = this.props;
    const {
      isTypeModalOpen,
      isQtyModalOpen,
      isDatesModalOpen,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      appliedRule,
    } = this.state;
    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    return (
      <>
        {/* TYPE MODAL */}
        {isTypeModalOpen && (
          <div
            className="nm-modal-overlay"
            onClick={() => this.closeModal("isTypeModalOpen")}
          >
            <div className="nm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="nm-modal-header">
                <button className="nm-modal-back" onClick={() => this.closeModal("isTypeModalOpen")}>
                  <ChevronLeft size={18} strokeWidth={2.5} />
                </button>
                <h5 className="nm-text-dark">
                  {t("Selecciona el tipo de taquilla")}
                </h5>
              </div>
              <div className="nm-modal-body">
                {this.renderLockers()}
              </div>
              <div className="nm-modal-footer"></div>
            </div>
          </div>
        )}

        {/* QTY MODAL */}
        {isQtyModalOpen && (
          <div
            className="nm-modal-overlay"
            onClick={() => this.closeModal("isQtyModalOpen")}
          >
            <div className="nm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="nm-modal-header">
                <button className="nm-modal-back" onClick={() => this.closeModal("isQtyModalOpen")}>
                  <ChevronLeft size={18} strokeWidth={2.5} />
                </button>
                <h5 className="nm-text-dark">{t("Cantidad de taquillas")}</h5>
              </div>
              <div className="nm-modal-body">
                <QuantityRentalStepper
                  min={1}
                  max={10}
                  value={qty}
                  label={t("Selecciona cuántas taquillas necesitas")}
                  onChange={(v) =>
                    this.setState({
                      selectedQuantity: v,
                      startDate: null,
                      endDate: null,
                      selectedPrices: [],
                      totalAmount: 0,
                      isDurationExceeded: false,
                      appliedRule: null,
                    })
                  }
                />
                <span>
                  {selectedQuantity}{" "}
                  {selectedQuantity > 1
                    ? t("taquillas seleccionadas")
                    : t("taquilla seleccionada")}
                </span>
              </div>
              <div className="nm-modal-footer">
                <button
                  className="nm-btn-continue"
                  onClick={() => this.closeModal("isQtyModalOpen")}
                >
                  {t("Confirmar")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DATES MODAL */}
        {isDatesModalOpen && (
          <div
            className="nm-modal-overlay"
            onClick={() => this.closeModal("isDatesModalOpen")}
          >
            <div
              className="nm-modal text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nm-modal-header">
                <button className="nm-modal-back" onClick={() => this.closeModal("isDatesModalOpen")}>
                  <ChevronLeft size={18} strokeWidth={2.5} />
                </button>
                <h5 className="nm-text-dark">{t("Selecciona las fechas")}</h5>
              </div>
              <div className="nm-modal-body">
                {/* Reuse your price calendar (mobile version here) */}
                {this.renderPriceCalendar()}
                {isDurationExceeded && (
                  <div className="text-center text-danger mt-2">
                    {t(
                      `El alquiler online está limitado a 14 días consecutivos...`
                    )}
                  </div>
                )}
              </div>
              <div className="nm-modal-footer">
                <div className="row">
                  <div
                    className="col"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label className="nm-uf-label">{t("Inicio")}</label>
                    <span>
                      {startDate
                        ? `${startDate.toLocaleDateString()}`
                        : t("DD/MM/YYYY")}
                    </span>
                  </div>
                  <vr class="bg-vw-light"></vr>
                  <div
                    className="col"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label className="nm-uf-label">{t("Fin")}</label>
                    <span>
                      {endDate
                        ? `${endDate.toLocaleDateString()}`
                        : t("DD/MM/YYYY")}
                    </span>
                  </div>
                </div>
                <hr></hr>
                {appliedRule && (
                  <span>
                    {t(
                      "Descuento aplicado: {{discount}}% (desde los {{days}} días)",
                      {
                        discount: appliedRule.DiscountPercentage,
                        days: appliedRule.MaxDays,
                      }
                    )}
                  </span>
                )}
                <br></br>
                <button
                  className="nm-btn-continue"
                  disabled={!startDate || isDurationExceeded}
                  onClick={() => this.closeModal("isDatesModalOpen")}
                >
                  {t("Confirmar")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  renderUnifiedFormPc() {
    const { t } = this.props;
    const {
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      totalAmount,
      appliedRule,
    } = this.state;

    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);
    const canContinue =
      selectedLockerType &&
      selectedQuantity &&
      startDate &&
      !isDurationExceeded;

    return (
      <>
        {/* FORM ROW */}
        <div className="nm-form-pc" ref={this.formRef}>
          <div
            className="nm-uf-row"
            style={{ display: "flex", gap: "12px" }}
          >
            {/* TYPE */}
            <div style={{ flex: 1 }}>
              <button
                ref={this.typeBtnRef}
                type="button"
                className="nm-uf-btn"
                onClick={() => this.openPanel("type")}
              >
                <span className="nm-uf-label">{t("Tipo")}</span>
                {selectedLockerType
                  ? this.getLockerType(selectedLockerType)
                  : t("Selecciona...")}
              </button>
            </div>

            <vr class="vr"></vr>

            {/* QUANTITY */}
            <div style={{ flex: 1 }}>
              <button
                ref={this.qtyBtnRef}
                type="button"
                className="nm-uf-btn"
                onClick={() => this.openPanel("qty")}
                disabled={(!selectedLockerType) || true}
              >
                <span className="nm-uf-label">{t("Cantidad")}</span>
                {qty} {qty === 1 ? " " + t("taquilla") : " " + t("taquillas")}
              </button>
            </div>

            <vr class="vr"></vr>

            {/* DATES */}
            <div style={{ flex: 1 }}>
              <button
                ref={this.datesStartBtnRef}
                type="button"
                className="nm-uf-btn"
                onClick={() => this.openPanel("dates")}
                disabled={(!selectedLockerType) || true}
              >
                <span className="nm-uf-label">{t("Inicio")}</span>
                {startDate
                  ? `${startDate.toLocaleDateString()}`
                  : t("DD/MM/YYYY")}
              </button>
            </div>

            <vr class="vr"></vr>

            <div style={{ flex: 1 }}>
              <button
                type="button"
                className="nm-uf-btn"
                onClick={() => this.openPanel("dates")}
                disabled={(!selectedLockerType || !startDate)  || true}
              >
                <span className="nm-uf-label">{t("Fin")}</span>
                {endDate ? `${endDate.toLocaleDateString()}` : t("DD/MM/YYYY")}
              </button>
            </div>

            {/*DISCOUNT*/}
            {appliedRule && (
              <span
                style={{
                  border: "1px solid #00000040",
                  padding: "10px",
                  borderRadius: "10px",
                  maxWidth: "200px",
                  maxHeight: "4vw",
                  marginTop: "0.5rem",
                }}
              >
                {t(
                  "Descuento aplicado: {{discount}}% (desde los {{days}} días)",
                  {
                    discount: appliedRule.DiscountPercentage,
                    days: appliedRule.MaxDays,
                  }
                )}
              </span>
            )}

            <vr class="vr"></vr>

            {/* CONTINUE BUTTON */}
            <button
              className={`nm-btn-continue ${
                !canContinue || this.state.isLoading ? "disabled" : ""
              }`}
              style={{ margin: "0" }}
              disabled={!canContinue || this.state.isLoading}
              onClick={() => this.handleContinue()}
            >
              {this.state.isLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <>
                  {t("Continuar")}
                  {t(" por ")}
                  {Number(this.getLockerPrice(selectedLockerType)).toFixed(2)}
                  {` ${currencySymbol()}`}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ERROR MESSAGE */}
        {this.state.errorMessage && (
          <div className="text-danger text-center">
            {t(
              "LO SENTIMOS PERO ESTAS FECHAS NO ESTÁN DISPONIBLES, PRUEBA OTRAS U OTRO TIPO DE TAQUILLAS."
            )}
          </div>
        )}

        {/* INLINE PANELS */}
        {this.renderUnifiedPanels()}
      </>
    );
  }

  renderUnifiedPanels(mode) {
    const { t } = this.props;
    const {
      isTypeModalOpen,
      isQtyModalOpen,
      isDatesModalOpen,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      activePanel,
      appliedRule,
      panelLeft,
    } = this.state;
    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    return (
      <>
        {activePanel === "type" && (
          <div className="nm-uf-panel show">
            <h5 className="nm-text-dark text-center mb-3">
              {t("Selecciona el tipo de taquilla")}
            </h5>
            {this.renderLockersPc()}
          </div>
        )}

        {activePanel === "qty" && (
          <div className="nm-uf-panel text-center show">
            <div className="nm-uf-modal-header">
              <h5 className="nm-text-dark">{t("Cantidad de taquillas")}</h5>
            </div>
            <div className="nm-modal-body">
              <QuantityRentalStepper
                min={1}
                max={10}
                value={qty}
                label={t("Selecciona cuántas taquillas necesitas")}
                onChange={(v) =>
                  this.setState({
                    selectedQuantity: v,
                    startDate: null,
                    endDate: null,
                    selectedPrices: [],
                    totalAmount: 0,
                    isDurationExceeded: false,
                    appliedRule: null,
                  })
                }
              />
            </div>
            <span>
              {selectedQuantity}{" "}
              {selectedQuantity > 1
                ? t("taquillas seleccionadas")
                : t("taquilla seleccionada")}
            </span>
          </div>
        )}

        {activePanel === "dates" && (
          <div className="nm-uf-panel text-center show">
            <h5 className="nm-text-dark">{t("Selecciona las fechas")}</h5>
            {isDurationExceeded && (
              <div className="text-center text-danger mt-2">
                {t(
                  `El alquiler online está limitado a 14 días consecutivos...`
                )}
              </div>
            )}
            {this.renderPriceCalendarPC()}
          </div>
        )}
      </>
    );
  }

  renderStepContent() {
    const { t } = this.props;
    const {
      step,
      lockers,
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      useUnifiedUI,
    } = this.state;

    if (useUnifiedUI) {
      // Single big form for mobile
      return this.renderUnifiedForm("mobile");
    }
  }

  renderStepContentPc() {
    const { t } = this.props;
    const {
      step,
      lockers,
      selectedLockerType,
      selectedQuantity,
      startDate,
      endDate,
      isDurationExceeded,
      useUnifiedUI,
    } = this.state;

    if (useUnifiedUI) {
      // Single big form for pc
      return this.renderUnifiedFormPc();
    }
  }

  render() {
    const { t } = this.props;
    const { lockers, prices } = this.state;
    const { zoneSelected, descriptionZone } = this.props.location.state;

    if (!lockers || !prices) {
      return (
        <div className="spinner">
          <img src="/images/logo.png" alt="Logo" />
        </div>
      );
    }

    const headerCard = (
      <div className="nm-page-header-card">
        <div className="nm-page-header-card-text">
          <h3>{t("RESERVA DE TAQUILLA")}</h3>
          <span className="nm-page-header-zone">{zoneSelected} — {descriptionZone}</span>
        </div>
        <Grid2x2Check className="nm-form-icon" strokeWidth={1} />
      </div>
    );

    return (
      <div className="normalBackground">
        <AppHeader />
        <div className="nm-page smartphone">
          {headerCard}
          {this.renderStepContent()}
          <NmStepBar step={2} />
        </div>

        <div className="nm-page pc">
          {headerCard}
          {this.renderStepContentPc()}
          <NmStepBar step={2} />
        </div>

        {this.renderMeasureModal()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: true,
  lockers: state.lockers,
  prices: state.prices,
});

const mapDispatchToProps = (dispatch) => ({
  push: bindActionCreators(push, dispatch),
  fetchLockers: bindActionCreators(fetchLockers, dispatch),
  postPrebooking: bindActionCreators(postPrebooking, dispatch),
});

export default withNamespaces()(
  connect(mapStateToProps, mapDispatchToProps)(LockerSelectorView)
);
